import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { useURLState } from '../hooks/useURLState';
import Navigation from "../components/Navigation";
import AssessmentStatusBadge from "../utils/assessmentStatusLabel";
import Modal from '../components/Modal';
import Card from '../components/Card';
import Button from '../components/Buttons';
import Input from '../components/Input';
import { useApplicationTypes } from "../contexts/ApplicationTypeContext";
import { useUserBases } from "../contexts/UserBaseContext";
import { useDeploymentModels } from "../contexts/DeploymentModelContext";
import LoadingSpinner from '../components/LoadingSpinner';
import { Plus, FileText, Shield, Building2, Store, Trash2, RefreshCw, Pencil, Users, Eye } from 'lucide-react';
import Pagination from "../components/Pagination";
import { usePageSize } from "../hooks/usePageSize";
import TPRMNewVendorModal from './TPRMNewVendorModal';
import { useRegions } from "../contexts/RegionContext";
import RegionField from "./shared/RegionField";
import { useVerticals } from "../contexts/VerticalsContext";
import AssignAssessModal from "../components/AssignAssessModal";
import AssessmentRespondentsModal from "../components/AssessmentRespondentsModal";
import AssessmentUnassignedModal from "../components/AssessmentUnassignedModal";
import DeleteAssessmentModal from "./DeleteAssessmentModal";
import ReassessModal from './ReassessModal';
import { useRBAC } from "../hooks/useRBAC";
import { useHasLicense } from "../contexts/LicenseContext";
import CreateBusinessUnitModal from './CreateBusinessUnitModal';
import AssessmentActivityLog from "../components/AssessmentActivityLog";
import IntelligenceNarrative from "../components/IntelligenceNarrative";
import DepartmentBusinessUnitMap from "./DepartmentBusinessUnitMap";

const BusinessUnits = () => {
  const [businessUnits, setBusinessUnits] = useState([]);
  const [applicationsWithRisks, setApplicationsWithRisks] = useState([]);
  const [selectedBusinessUnitId, setSelectedBusinessUnitId] = useURLState('businessUnitId');
  const [currentPageParam, setCurrentPageParam] = useURLState('page');
  const currentPage = parseInt(currentPageParam) || 1;
  const [showNewBusinessUnitModal, setShowNewBusinessUnitModal] = useState(false);
  const [showNewApplicationModal, setShowNewApplicationModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const selectedBusinessUnit = businessUnits.find(bu => String(bu.businessUnitId) === selectedBusinessUnitId) || null;
  const [totalCount, setTotalCount] = useState(0);
  const [paging, setPaging] = useState(false);
  const [pageSize, setPageSize] = usePageSize('businessUnits');
  const listTopRef = useRef(null);
  const applicationTypes = useApplicationTypes() || [];
  const userBases = useUserBases() || [];
  const deploymentModels = useDeploymentModels() || [];

  const navigate = useNavigate();
  const location = useLocation();
  const apiCall = useApi();
  const { user } = useAuth();

  const regions = useRegions();
  const [formData, setFormData] = useState({
    regionId: '',
    appScope: '',
    vendorId: '',
  });

  const { verticals, subVerticals } = useVerticals();
  const { canAssess, getAssessDisabledReason, hasMinimumRoleAtOrAbove, canManageOrganizationRisks, canViewBusinessUnit, canAssessViaAssignment, canCreateDealOrBusinessUnit, canDeleteAssessments, authVersion } = useRBAC();
  const selectedOrg = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const canManageRisks = canManageOrganizationRisks();
  const canManageAppsForSelectedBU = canManageRisks ||
    (selectedBusinessUnit && hasMinimumRoleAtOrAbove('ADMIN', 'BUSINESS_UNIT', selectedBusinessUnit?.businessUnitId));
  const canAddDealOrBU = canCreateDealOrBusinessUnit(selectedOrg?.organizationId);
  const canDeleteAssessment = canDeleteAssessments(); // super admin only
  const hasLicense = useHasLicense();
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [respondentsAssessmentId, setRespondentsAssessmentId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showReassessModal, setShowReassessModal] = useState(false);
  const [assessmentToReassess, setAssessmentToReassess] = useState(null);
  const [reassessing, setReassessing] = useState(false);
  const [showUnassignedAlert, setShowUnassignedAlert] = useState(false);
  const [showEditApplicationModal, setShowEditApplicationModal] = useState(false);
  const [editingApplication, setEditingApplication] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const rightPanelRef = useRef(null);
  const fetchIdRef = useRef(0);
  // Tracks the businessUnitId we've already attempted to page-locate, so we only
  // walk pages once per selection (and clear if the user later pages away from it).
  const locateAttemptedForRef = useRef(null);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [showNewVendorModal, setShowNewVendorModal] = useState(false);

  const fetchVendors = async () => {
    setVendorsLoading(true);
    try {
      const data = await apiCall("GET", "/TPRMVendor/vendors?pageSize=100");
      setVendors(data.items || []);
    } catch (err) {
      setVendors([]);
    } finally {
      setVendorsLoading(false);
    }
  };

  // Reset application state when user/auth changes. Skip the initial mount so we don't
  // clobber a businessUnitId arriving in the URL (e.g. from a "Recent Business Units" click).
  const prevAuthVersionRef = useRef(authVersion);
  useEffect(() => {
    if (prevAuthVersionRef.current === authVersion) return;
    prevAuthVersionRef.current = authVersion;
    setApplicationsWithRisks([]);
    setSelectedBusinessUnitId(null);
  }, [authVersion]);

  useEffect(() => {
    fetchBusinessUnits();
    fetchVendors();
  }, [currentPage, pageSize, authVersion]);

  const refreshApplications = async () => {
    if (!selectedBusinessUnit) return;
    fetchApplications(selectedBusinessUnit.businessUnitId);
  };

  const handleDeleteAssessment = async () => {
    if (!assessmentToDelete) return;
    setDeleting(true);
    try {
      await apiCall('DELETE', `/businessUnitAssessments/${assessmentToDelete}`);
      setShowDeleteModal(false);
      setAssessmentToDelete(null);
      await fetchApplications(selectedBusinessUnit.businessUnitId);
    } catch (error) {} finally {
      setDeleting(false);
    }
  };

  const handleReassessAssessment = async () => {
    if (!assessmentToReassess) return;
    setReassessing(true);
    try {
      await apiCall('POST', `/businessUnitAssessments/${assessmentToReassess}/reassess`);
      setShowReassessModal(false);
      setAssessmentToReassess(null);
      navigate(`/businessunitassessment/${assessmentToReassess}?step=review`);
    } catch (error) {} finally {
      setReassessing(false);
    }
  };

  // Auto-select from navigation state
  useEffect(() => {
    const autoSelectId = location.state?.selectedBusinessUnitId;
    if (autoSelectId && businessUnits.length > 0) {
      const target = businessUnits.find(bu => bu.businessUnitId === autoSelectId);
      if (target) {
        setSelectedBusinessUnitId(target.businessUnitId);
      }
      navigate('/businessunits', { replace: true });
    }
  }, [businessUnits]);

  // Auto-fetch applications when URL has businessUnitId and BUs are loaded (bookmark/refresh,
  // or arriving from a "Recent Business Units" click on the dashboard). If the BU isn't on the
  // current page, page through the list once to find and jump to its page.
  useEffect(() => {
    if (!selectedBusinessUnitId || businessUnits.length === 0) return;
    const foundInPage = businessUnits.find(bu => String(bu.businessUnitId) === selectedBusinessUnitId);
    if (foundInPage) {
      locateAttemptedForRef.current = selectedBusinessUnitId;
      fetchApplications(foundInPage.businessUnitId);
    } else if (locateAttemptedForRef.current !== selectedBusinessUnitId) {
      // First time we notice this BU isn't on the current page — try to page to it.
      locateAttemptedForRef.current = selectedBusinessUnitId;
      locateBusinessUnitPage(selectedBusinessUnitId);
    } else {
      // Already located (or the user paged away) — clear the stale selection.
      setSelectedBusinessUnitId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusinessUnitId, businessUnits]);

  // Walk the business unit list (using the server's own paging) to find which page
  // a selected BU lives on, then navigate to that page.
  const locateBusinessUnitPage = async (businessUnitId) => {
    try {
      let p = 1;
      while (true) {
        const data = await apiCall("GET", `/businessunits/summary?page=${p}&pageSize=${pageSize}`);
        const items = data.items || [];
        if (items.length === 0) break; // past the last page
        if (items.some(bu => String(bu.businessUnitId) === String(businessUnitId))) {
          setCurrentPageParam(p); // effect re-runs after fetchBusinessUnits reloads this page
          return;
        }
        if (data.totalCount && p * pageSize >= data.totalCount) break;
        p++;
      }
      // Not found on any page — clear the stale selection.
      setSelectedBusinessUnitId(null);
    } catch (e) {
      setSelectedBusinessUnitId(null);
    }
  };

  const fetchBusinessUnits = async () => {
    setPaging(true);
    try {
      const org = JSON.parse(localStorage.getItem("selectedOrganization"));
      if (!org?.organizationId) {
        return;
      }

      // Use the optimized summary endpoint with paging
      const data = await apiCall("GET", `/businessunits/summary?page=${currentPage}&pageSize=${pageSize}`);

      // Map response to match existing component shape
      const businessUnitsWithCounts = (data.items || []).map((bu) => ({
        businessUnitId: bu.businessUnitId,
        businessUnitName: bu.businessUnitName,
        verticalId: bu.verticalId,
        domain: bu.domain,
        regionId: bu.regionId,
        applicationCount: bu.applicationCount,
        RiskLevel: bu.riskLevel === "NONE" ? "No RISK" : bu.riskLevel,
        riskCounts: {
          CRITICAL: bu.riskCounts?.critical || 0,
          HIGH: bu.riskCounts?.high || 0,
          MEDIUM: bu.riskCounts?.medium || 0,
          LOW: bu.riskCounts?.low || 0,
        },
      }));

      // Backend /businessunits/summary already applies RBAC BU-scoping and returns a scoped
      // totalCount, so trust the server response directly. Re-filtering client-side broke
      // pagination for BU-scoped users by clobbering totalCount with the current page's length.
      setBusinessUnits(businessUnitsWithCounts);
      setTotalCount(data.totalCount || 0);

    } catch (error) {} finally {
      setLoading(false);
      setPaging(false);
    }
  };

  const fetchApplications = async (businessUnitId) => {
    const thisFetchId = ++fetchIdRef.current;
    setApplicationsLoading(true);
    try {
      const summaries = await apiCall("GET", `/businessunitassessments/summary?businessUnitId=${businessUnitId}`);
      if (thisFetchId !== fetchIdRef.current) return;

      const appsWithRisks = summaries.map((s) => ({
        businessUnitApplicationId: s.businessUnitApplicationId,
        applicationName: s.applicationName,
        applicationDescription: s.applicationDescription,
        targetEndDate: s.targetEndDate,
        applicationTypeId: s.applicationTypeId,
        regionId: s.regionId,
        verticalId: s.verticalId,
        subVerticalId: s.subVerticalId,
        userBaseId: s.userBaseId,
        deploymentModelId: s.deploymentModelId,
        vendorId: s.vendorId,
        applicationOwner: s.applicationOwner,
        criticality: s.criticality,
        appScope: s.appScope,
        detailConfirmationPending: s.detailConfirmationPending,
        businessUnitId: s.businessUnitId,
        assignedToUserId: s.assignedToUserId,
        assignedToUserName: s.assignedToUserName,
        assessmentAssignedToUserId: s.assessmentAssignedToUserId,
        assigneeUserIds: s.assigneeUserIds,
        hasAssessment: s.hasAssessment,
        assessmentID: s.assessmentID,
        assessmentStatus: s.status,
        riskCounts: {
          CRITICAL: s.riskCounts?.critical || 0,
          HIGH: s.riskCounts?.high || 0,
          MEDIUM: s.riskCounts?.medium || 0,
          LOW: s.riskCounts?.low || 0,
        },
        totalRisks: s.totalRisks,
        activeRisks: s.activeRisks,
        riskScore: s.riskScore,
      }));

      setApplicationsWithRisks(appsWithRisks);
    } catch (error) {
      if (thisFetchId !== fetchIdRef.current) return;
    } finally {
      if (thisFetchId === fetchIdRef.current) {
        setApplicationsLoading(false);
      }
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />

      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-content-primary">Business Unit Management</h1>
          <p className="mt-2 text-gray-600">
            Manage Business Units and Applications for security risk assessment
          </p>
        </div>

        {/* Intelligence Narrative — RISK_MANAGER+ at ORG only */}
        {canManageRisks && (
          <IntelligenceNarrative
            type="bu"
            title="Intelligence Narrative"
            subtitle="AI-generated briefing based on business unit risks"
            accentColor="blue"
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Business Units */}
          <Card>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Business Units</h2>
              {canAddDealOrBU && (
                <Button
                  size="sm"
                  onClick={() => setShowNewBusinessUnitModal(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Business Unit
                </Button>
              )}
            </div>

            <div className="space-y-3" ref={listTopRef}>
              {businessUnits.map((bu) => (
                <div
                  key={bu.businessUnitId}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${selectedBusinessUnit?.businessUnitId === bu.businessUnitId
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                    }`}
                  onClick={() => {
                    setSelectedBusinessUnitId(bu.businessUnitId);
                    fetchApplications(bu.businessUnitId);
                    rightPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-medium text-content-primary">{bu.businessUnitName}</h3>
                      <p className="text-sm text-gray-600">{verticals.find(v => v.verticalId === bu.verticalId)?.verticalName || "N/A"}</p>
                    </div>
                    <div className="ml-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          {bu.applicationCount || 0} Apps
                        </span>
                      </div>
                      {bu.domain && (
                        <div className="text-xs text-gray-500 mt-1">
                          {bu.domain}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Risk Bar - Placeholder for now */}
                  <div className="mt-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-gray-600">Risk Level</span>
                      <span className="text-xs font-medium text-content-primary">
                        {bu.RiskLevel || 'No RISK'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${bu.RiskLevel === 'CRITICAL'
                          ? 'bg-red-700'
                          : bu.RiskLevel === 'HIGH'
                            ? 'bg-orange-500'
                            : bu.RiskLevel === 'MEDIUM'
                              ? 'bg-yellow-500'
                              : bu.RiskLevel === 'LOW'
                                ? 'bg-green-500'
                                : 'bg-gray-400'
                          }`}
                        style={{
                          width: `${bu.RiskLevel === 'CRITICAL'
                            ? 90
                            : bu.RiskLevel === 'HIGH'
                              ? 75
                              : bu.RiskLevel === 'MEDIUM'
                                ? 50
                                : bu.RiskLevel === 'LOW'
                                  ? 25
                                  : 0
                            }%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              totalItems={totalCount}
              pageSize={pageSize}
              onPageChange={setCurrentPageParam}
              onPageSizeChange={setPageSize}
              scrollTargetRef={listTopRef}
              busy={paging}
            />
          </Card>

          {/* Applications */}
          <Card ref={rightPanelRef}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Applications</h2>
              {selectedBusinessUnit && (
                <div className="flex space-x-2">
                  {canManageAppsForSelectedBU && hasLicense('Applications') && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => setShowNewApplicationModal(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      New Application
                    </Button>
                  )}
                </div>
              )}
            </div>

            {selectedBusinessUnit ? (
              applicationsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : applicationsWithRisks.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No applications associated with this business unit.
                </div>
              ) : (
                <div className="space-y-3">
                  {[...applicationsWithRisks].sort((a, b) =>
                    (a.applicationName || '').localeCompare(b.applicationName || '')
                  ).map((application) => (
                    <div
                      key={application.businessUnitApplicationId}
                      className="p-4 border border-gray-200 rounded-lg hover:border-gray-300"
                    >
                      {/* Title and description row */}
                      <div>
                        <div className="flex items-center gap-1.5">
                          <h3 className="font-medium text-content-primary">{application.applicationName}<AssessmentStatusBadge status={application.assessmentStatus} /></h3>
                          {canManageAppsForSelectedBU && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const canEditAll = !application.hasAssessment || application.detailConfirmationPending;
                                setEditingApplication(application);
                                setEditFormData({
                                  applicationName: application.applicationName || '',
                                  applicationOwner: application.applicationOwner || '',
                                  targetEndDate: application.targetEndDate ? new Date(application.targetEndDate).toISOString().split('T')[0] : '',
                                  criticality: application.criticality || 'MEDIUM',
                                  applicationDescription: application.applicationDescription || '',
                                  appScope: application.appScope || '',
                                  vendorId: application.vendorId?.toString() || '',
                                  verticalId: application.verticalId || '',
                                  subVerticalId: application.subVerticalId || '',
                                  applicationTypeId: application.applicationTypeId?.toString() || '',
                                  userBaseId: application.userBaseId?.toString() || '',
                                  deploymentModelId: application.deploymentModelId?.toString() || '',
                                  canEditAll,
                                });
                                setShowEditApplicationModal(true);
                                if (vendors.length === 0) fetchVendors();
                              }}
                              className="text-gray-400 hover:text-blue-600 transition-colors"
                              title="Edit application"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                        {application.applicationDescription && (
                          <p className="text-sm text-gray-500 mt-1">{application.applicationDescription}</p>
                        )}
                        {application.targetEndDate && (
                          <p className="text-xs text-gray-500 mt-1">
                            Due: {new Date(application.targetEndDate).toLocaleDateString()}
                          </p>
                        )}
                        {application.assignedToUserName && (
                          <p className="text-xs text-blue-600 mt-1">
                            Assigned to: <span className="font-semibold">{application.assignedToUserName}</span>
                          </p>
                        )}
                      </div>

                      {/* Actions row */}
                      <div className="flex flex-wrap gap-2 mt-3">
                        {canManageAppsForSelectedBU && (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={
                              application.hasAssessment &&
                              (application.assessmentStatus === 'completed' || application.assessmentStatus === 'Completed')
                            }
                            onClick={() => {
                              setSelectedApplication(application);
                              setShowAssignModal(true);
                            }}
                          >
                            <Shield className="h-3 w-3 mr-1" />
                            {application.hasAssessment && (application.assessmentStatus === 'completed' || application.assessmentStatus === 'Completed')
                              ? "Assign"
                              : application.assignedToUserName ? "Reassign" : "Assign"}
                          </Button>
                        )}

                        {canManageAppsForSelectedBU && application.assessmentID && (
                          <Button
                            size="sm"
                            variant="secondary"
                            className="whitespace-nowrap"
                            title="Manage additional respondents for this assessment"
                            onClick={() => setRespondentsAssessmentId(application.assessmentID)}
                          >
                            <Users className="h-4 w-4 mr-1" />
                            Respondents
                          </Button>
                        )}

                        {/* Assess Button */}
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={
                            !canAssess(application.assessmentAssignedToUserId, null, 'BUSINESS_UNIT', selectedBusinessUnit?.businessUnitId, application.assigneeUserIds || []) ||
                            (application.hasAssessment &&
                              (application.assessmentStatus === 'completed' || application.assessmentStatus === 'Completed'))
                          }
                          onClick={() => {
                            if (!application.assessmentAssignedToUserId) {
                              setShowUnassignedAlert(true);
                              return;
                            }
                            const buAssessPath = application.assessmentID
                              ? `/businessunitassessment/${application.assessmentID}`
                              : '/businessunitassessment';
                            navigate(buAssessPath, {
                              state: {
                                businessUnitApplicationId: application.businessUnitApplicationId,
                                applicationName: application.applicationName,
                                applicationTypeId: application.applicationTypeId,
                                regionId: application.regionId,
                                verticalId: application.verticalId,
                                subVerticalId: application.subVerticalId,
                                userBaseId: application.userBaseId,
                                deploymentModelId: application.deploymentModelId,
                                businessUnitId: application.businessUnitId,
                              }
                            });
                          }}
                          title={
                            (application.hasAssessment &&
                                (application.assessmentStatus === 'completed' || application.assessmentStatus === 'Completed')
                                ? "Assessment is completed"
                                : "")
                              || getAssessDisabledReason(application.assessmentAssignedToUserId, null, 'BUSINESS_UNIT', selectedBusinessUnit?.businessUnitId, application.assigneeUserIds || [])
                              || "Start Assessment"
                          }
                        >
                          <FileText className="h-3 w-3 mr-1" />
                          Assess
                        </Button>

                        {/* Viewing mirrors the backend's viewable-assessment rule: BU scope or assignment. */}
                        {application.hasAssessment && application.assessmentID && (application.assessmentStatus === 'completed' || application.assessmentStatus === 'Completed') &&
                          (canViewBusinessUnit(selectedBusinessUnit?.businessUnitId) || canAssessViaAssignment(application.assessmentAssignedToUserId, application.assigneeUserIds || [])) && (
                          <Button
                            size="sm"
                            variant="secondary"
                            title="View the completed assessment's questions, answers, notes, and attachments (read-only)"
                            onClick={() => navigate(`/businessunitassessment/${application.assessmentID}?step=review`)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Answers
                          </Button>
                        )}

                        {/* View Risks Button */}
                        <Button
                          size="sm"
                          variant="primary"
                          className="px-1 py-1 h-9 text-xs"
                          disabled={!application.hasAssessment || (application.assessmentStatus !== 'completed' && application.assessmentStatus !== 'Completed')}
                          onClick={() => {
                            if (application.assessmentID) {
                              navigate(`/businessunitassessment/${application.assessmentID}/risks`);
                            }
                          }}
                          title={application.hasAssessment && (application.assessmentStatus === 'completed' || application.assessmentStatus === 'Completed') ? "View Risks" : "Complete assessment to view risks"}
                        >
                          <Shield className="h-4 w-4 mr-1" />
                          View Risks ({application.activeRisks ?? application.totalRisks ?? 0})
                        </Button>
                        {canManageOrganizationRisks() && application.assessmentID && (
                          <Button size="sm" variant="warning" className="px-1 py-1 h-9 text-xs"
                            disabled={application.assessmentStatus !== 'completed'}
                            onClick={(e) => {
                              e.stopPropagation();
                              setAssessmentToReassess(application.assessmentID);
                              setShowReassessModal(true);
                            }}>
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Reassess
                          </Button>
                        )}
                        {canDeleteAssessment && application.assessmentID && (
                          <Button size="sm" variant="danger" className="px-1 py-1 h-9 text-xs"
                            onClick={(e) => { e.stopPropagation(); setAssessmentToDelete(application.assessmentID); setShowDeleteModal(true); }}>
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        )}
                      </div>

                      {/* Assessment status details */}
                      {application.hasAssessment ? (
                        <div className="mt-3">
                          <div className="flex flex-wrap gap-2">
                            {application.riskCounts.CRITICAL > 0 && (
                              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">
                                {application.riskCounts.CRITICAL} Critical
                              </span>
                            )}
                            {application.riskCounts.HIGH > 0 && (
                              <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">
                                {application.riskCounts.HIGH} High
                              </span>
                            )}
                            {application.riskCounts.MEDIUM > 0 && (
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                                {application.riskCounts.MEDIUM} Medium
                              </span>
                            )}
                            {application.riskCounts.LOW > 0 && (
                              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                {application.riskCounts.LOW} Low
                              </span>
                            )}
                          </div>
                          <div className="mt-2">
                            <div className="text-xs text-gray-500 mb-1">
                              Risk Score: {application.riskScore || 0}%
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-4">
                              <div
                                className={`h-4 rounded-full ${
                                  (application.riskScore || 0) <= 40 ? 'bg-green-500'
                                  : (application.riskScore || 0) <= 60 ? 'bg-yellow-500'
                                  : (application.riskScore || 0) <= 80 ? 'bg-orange-500'
                                  : 'bg-red-500'
                                }`}
                                style={{ width: `${application.riskScore || 0}%` }}
                              ></div>
                            </div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {application.totalRisks || 0} Total Risks
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3 text-xs text-gray-500">No Assessment Data</div>
                      )}

                      {application.assessmentID && (
                        <div className="flex justify-end mt-1">
                          <AssessmentActivityLog assessmentType="businessunit" assessmentId={String(application.assessmentID)} hideQuestionEvents />
                        </div>
                      )}

                    </div>
                  ))}
                </div>
              )
            ) : (
              <div className="text-center py-8">
                <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Select a business unit to view applications</p>
              </div>
            )}
          </Card>
        </div>

        {/* Fusion department mapping: self-gated on the Fusion flag + canManageOrganizationRisks */}
        <DepartmentBusinessUnitMap organizationId={selectedOrg?.organizationId} />
      </div>

      <CreateBusinessUnitModal
        isOpen={showNewBusinessUnitModal}
        onClose={() => setShowNewBusinessUnitModal(false)}
        onBusinessUnitCreated={() => { setCurrentPageParam(1); fetchBusinessUnits(); }}
      />
      {showNewApplicationModal && selectedBusinessUnit && (
        <Modal
          isOpen={showNewApplicationModal}
          onClose={() => setShowNewApplicationModal(false)}
          title="New Application"
        >
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setIsSubmitting(true);
              try {
                const fd = new FormData(e.target);
                const org = JSON.parse(localStorage.getItem("selectedOrganization"));

                const submitData = {
                  applicationName: fd.get("applicationName"),
                  applicationOwner: fd.get("applicationOwner"),
                  applicationDescription: fd.get("applicationDescription"),
                  targetEndDate: fd.get("targetEndDate")
                    ? new Date(fd.get("targetEndDate")).toISOString()
                    : null,
                  businessUnitId: selectedBusinessUnit.businessUnitId,
                  regionId: parseInt(fd.get("regionId")),
                  verticalId: parseInt(fd.get("verticalId")),
                  subVerticalId: parseInt(fd.get("subVerticalId")),
                  organizationId: org?.organizationId,
                  tenantId: org?.tenantId,
                  criticality: fd.get("criticality"),
                  applicationTypeId: parseInt(fd.get("applicationTypeId")) || null,
                  userBaseId: parseInt(fd.get("userBaseId")) || null,
                  deploymentModelId: parseInt(fd.get("deploymentModelId")) || null,
                  vendorId: parseInt(formData.vendorId) || null,
                  appScope: formData.appScope || null,
                };

                await apiCall("POST", "/businessunitapplications", submitData);
                setShowNewApplicationModal(false);
                fetchApplications(selectedBusinessUnit.businessUnitId);
              } catch (error) {
                const msg = error.response?.data?.message || "Error creating application.";
                alert(msg);
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {/* Internal / External */}
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">Application Scope</label>
              <select
                value={formData.appScope}
                onChange={e => setFormData({
                  ...formData,
                  appScope: e.target.value,
                  vendorId: e.target.value !== 'External' ? '' : formData.vendorId,
                })}
                className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">-- Select Scope --</option>
                <option value="Internal">Internal</option>
                <option value="External">External</option>
              </select>
            </div>

            {/* Vendor */}
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">Vendor</label>
              <div className="flex gap-2">
                <select
                  value={formData.vendorId}
                  onChange={e => setFormData({ ...formData, vendorId: e.target.value })}
                  disabled={formData.appScope !== 'External'}
                  className="flex-1 p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">-- Select Vendor --</option>
                  {[...vendors].sort((a, b) => (a.vendorName || '').localeCompare(b.vendorName || '')).map(v => (
                    <option key={v.vendorId} value={v.vendorId}>{v.vendorName}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowNewVendorModal(true)}
                  disabled={formData.appScope !== 'External'}
                  className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Add new vendor"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">Application Type</label>
              <select
                name="applicationTypeId"
                value={formData.applicationTypeId}
                onChange={e => setFormData({ ...formData, applicationTypeId: e.target.value })}
                className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">-- Select Application Type --</option>
                {applicationTypes.map(a => (
                  <option key={a.applicationTypeId} value={a.applicationTypeId}>{a.applicationTypeName}</option>
                ))}
              </select>
            </div>

            {/* Application Name */}
            <Input label="Application Name" name="applicationName" required />

            <Input label="Application Owner" name="applicationOwner" required />

            {/* User Base */}
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">User Base</label>
              <select
                name="userBaseId"
                value={formData.userBaseId}
                onChange={(e) => setFormData({ ...formData, userBaseId: e.target.value })}
                className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">-- Select User Base --</option>
                {userBases.map((u) => (
                  <option key={u.userBaseId} value={u.userBaseId}>
                    {u.userBaseTitle}
                  </option>
                ))}
              </select>
            </div>

            {/* Deployment Model */}
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">Deployment Model</label>
              <select
                name="deploymentModelId"
                value={formData.deploymentModelId}
                onChange={e => setFormData({ ...formData, deploymentModelId: e.target.value })}
                className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">-- Select Deployment Model --</option>
                {deploymentModels.map(dm => (
                  <option key={dm.deploymentModelId} value={dm.deploymentModelId}>
                    {dm.deploymentModelTitle}
                  </option>
                ))}
              </select>
            </div>

            {/* Region Select */}
            <RegionField
              regions={regions}
              name="regionId"
              defaultValue={selectedBusinessUnit?.regionId}
              label="Data Residency Region"
              className="mb-4"
              labelClassName="block text-sm font-medium text-content-secondary mb-1"
            />

            {/* Vertical Select */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-content-secondary mb-1">
                Vertical <span className="text-red-500">*</span>
              </label>
              <select
                name="verticalId"
                value={formData.VerticalId}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    VerticalId: parseInt(e.target.value),
                    SubVerticalId: ""
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                required
              >
                <option value="">-- Select Vertical --</option>
                {verticals.map((v) => (
                  <option key={v.verticalId} value={v.verticalId}>
                    {v.verticalName}
                  </option>
                ))}
              </select>
            </div>

            {/* SubVertical Select */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-content-secondary mb-1">
                SubVertical <span className="text-red-500">*</span>
              </label>
              <select
                name="subVerticalId"
                value={formData.SubVerticalId}
                onChange={(e) =>
                  setFormData({ ...formData, SubVerticalId: parseInt(e.target.value) })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50"
                required
                disabled={!formData.VerticalId}
              >
                <option value="">-- Select Sub-Vertical --</option>
                {verticals
                  .find((v) => v.verticalId === parseInt(formData.VerticalId))
                  ?.subVerticals?.map((sv) => (
                    <option key={sv.subVerticalId} value={sv.subVerticalId}>
                      {sv.subVerticalName}
                    </option>
                  ))}
              </select>
            </div>

            {/* Target End Date */}
            <Input label="Target End Date" name="targetEndDate" type="date" />

            {/* Criticality */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-content-secondary mb-1">
                Criticality
              </label>
              <select
                name="criticality"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="LOW">Low</option>
                <option value="MEDIUM" selected>
                  Medium
                </option>
                <option value="HIGH">High</option>
                <option value="CRITICAL">Critical</option>
              </select>
            </div>

            {/* Description */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-content-secondary mb-1">
                Description
              </label>
              <textarea
                name="applicationDescription"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowNewApplicationModal(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  "Create Application"
                )}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Create Vendor Modal — rendered outside the application <form> so its submit
          event does not bubble through the React tree into the app-creation handler */}
      {showNewVendorModal && (
        <TPRMNewVendorModal
          isOpen={showNewVendorModal}
          onClose={() => setShowNewVendorModal(false)}
          onVendorCreated={(newVendor) => {
            setVendors(prev => [...prev, newVendor]);
            setFormData(prev => ({ ...prev, vendorId: String(newVendor.vendorId) }));
            setShowNewVendorModal(false);
          }}
        />
      )}

      {showAssignModal && selectedApplication && (
        <AssignAssessModal
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          applicationId={selectedApplication.businessUnitApplicationId}
          refreshData={refreshApplications}
          apiEndpoint={`/businessunitapplications/${selectedApplication.businessUnitApplicationId}/assign`}
        />
      )}

      <AssessmentRespondentsModal
        isOpen={respondentsAssessmentId != null}
        onClose={() => setRespondentsAssessmentId(null)}
        assessmentType="businessunit"
        assessmentId={respondentsAssessmentId}
        refreshData={refreshApplications}
      />

      <DeleteAssessmentModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setAssessmentToDelete(null); }}
        onConfirm={handleDeleteAssessment}
        isDeleting={deleting}
      />

      <ReassessModal
        isOpen={showReassessModal}
        onClose={() => { setShowReassessModal(false); setAssessmentToReassess(null); }}
        onConfirm={handleReassessAssessment}
        isReassessing={reassessing}
      />

      {showEditApplicationModal && editingApplication && (
        <Modal
          isOpen={showEditApplicationModal}
          onClose={() => { setShowEditApplicationModal(false); setEditingApplication(null); }}
          title="Edit Application"
        >
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setIsSubmitting(true);
              try {
                const submitData = {
                  applicationName: editFormData.applicationName,
                  applicationOwner: editFormData.applicationOwner,
                  applicationDescription: editFormData.applicationDescription,
                  targetEndDate: editFormData.targetEndDate
                    ? new Date(editFormData.targetEndDate).toISOString()
                    : null,
                  criticality: editFormData.criticality,
                  appScope: editFormData.appScope || null,
                  vendorId: parseInt(editFormData.vendorId) || null,
                  verticalId: parseInt(editFormData.verticalId) || null,
                  subVerticalId: parseInt(editFormData.subVerticalId) || null,
                  applicationTypeId: parseInt(editFormData.applicationTypeId) || null,
                  userBaseId: parseInt(editFormData.userBaseId) || null,
                  deploymentModelId: parseInt(editFormData.deploymentModelId) || null,
                };

                await apiCall("PUT", `/businessunitapplications/${editingApplication.businessUnitApplicationId}`, submitData);
                setShowEditApplicationModal(false);
                setEditingApplication(null);
                fetchApplications(selectedBusinessUnit.businessUnitId);
              } catch (error) {
                const msg = error.response?.data?.message || "Error updating application.";
                alert(msg);
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {/* Application Scope */}
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">Application Scope</label>
              <select
                value={editFormData.appScope}
                onChange={e => setEditFormData({
                  ...editFormData,
                  appScope: e.target.value,
                  vendorId: e.target.value !== 'External' ? '' : editFormData.vendorId,
                })}
                className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">-- Select Scope --</option>
                <option value="Internal">Internal</option>
                <option value="External">External</option>
              </select>
            </div>

            {/* Vendor */}
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">Vendor</label>
              <select
                value={editFormData.vendorId || ""}
                onChange={e => setEditFormData({ ...editFormData, vendorId: e.target.value })}
                disabled={vendorsLoading || editFormData.appScope !== 'External'}
                className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">-- Select Vendor --</option>
                {vendors
                  .slice()
                  .sort((a, b) => a.vendorName.localeCompare(b.vendorName))
                  .map(v => (
                    <option key={v.vendorId} value={v.vendorId}>{v.vendorName}</option>
                  ))}
              </select>
            </div>

            {/* Application Type */}
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">Application Type</label>
              <select
                value={editFormData.applicationTypeId}
                onChange={e => setEditFormData({ ...editFormData, applicationTypeId: e.target.value })}
                disabled={!editFormData.canEditAll}
                className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">-- Select Application Type --</option>
                {applicationTypes.map(a => (
                  <option key={a.applicationTypeId} value={a.applicationTypeId}>{a.applicationTypeName}</option>
                ))}
              </select>
            </div>

            {/* Application Name */}
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Application Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editFormData.applicationName}
                onChange={e => setEditFormData({ ...editFormData, applicationName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Application Owner */}
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">Application Owner</label>
              <input
                type="text"
                value={editFormData.applicationOwner}
                onChange={e => setEditFormData({ ...editFormData, applicationOwner: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* User Base */}
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">User Base</label>
              <select
                value={editFormData.userBaseId}
                onChange={e => setEditFormData({ ...editFormData, userBaseId: e.target.value })}
                disabled={!editFormData.canEditAll}
                className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">-- Select User Base --</option>
                {userBases.map(u => (
                  <option key={u.userBaseId} value={u.userBaseId}>{u.userBaseTitle}</option>
                ))}
              </select>
            </div>

            {/* Deployment Model */}
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">Deployment Model</label>
              <select
                value={editFormData.deploymentModelId}
                onChange={e => setEditFormData({ ...editFormData, deploymentModelId: e.target.value })}
                disabled={!editFormData.canEditAll}
                className="block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">-- Select Deployment Model --</option>
                {deploymentModels.map(dm => (
                  <option key={dm.deploymentModelId} value={dm.deploymentModelId}>{dm.deploymentModelTitle}</option>
                ))}
              </select>
            </div>

            {/* Region (always disabled) */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Residency Region</label>
              <select
                value={editingApplication.regionId}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {regions.map(r => (
                  <option key={r.regionId} value={r.regionId}>{r.regionName}</option>
                ))}
              </select>
            </div>

            {/* Vertical */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Vertical</label>
              <select
                value={editFormData.verticalId}
                onChange={e => setEditFormData({
                  ...editFormData,
                  verticalId: parseInt(e.target.value),
                  subVerticalId: '',
                })}
                disabled={!editFormData.canEditAll}
                className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">-- Select Vertical --</option>
                {verticals.map(v => (
                  <option key={v.verticalId} value={v.verticalId}>{v.verticalName}</option>
                ))}
              </select>
            </div>

            {/* SubVertical */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">SubVertical</label>
              <select
                value={editFormData.subVerticalId}
                onChange={e => setEditFormData({ ...editFormData, subVerticalId: parseInt(e.target.value) })}
                disabled={!editFormData.canEditAll || !editFormData.verticalId}
                className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">-- Select Sub-Vertical --</option>
                {verticals
                  .find(v => v.verticalId === parseInt(editFormData.verticalId))
                  ?.subVerticals?.map(sv => (
                    <option key={sv.subVerticalId} value={sv.subVerticalId}>{sv.subVerticalName}</option>
                  ))}
              </select>
            </div>

            {/* Target End Date */}
            <div className="mb-4">
              <label className="block mb-1 text-sm font-medium text-gray-700">Target End Date</label>
              <input
                type="date"
                value={editFormData.targetEndDate}
                onChange={e => setEditFormData({ ...editFormData, targetEndDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Criticality */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Criticality</label>
              <select
                value={editFormData.criticality}
                onChange={e => setEditFormData({ ...editFormData, criticality: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="CRITICAL">Critical</option>
              </select>
            </div>

            {/* Description */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={editFormData.applicationDescription}
                onChange={e => setEditFormData({ ...editFormData, applicationDescription: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
              />
            </div>

            {!editFormData.canEditAll && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <p className="text-xs text-amber-700">
                  Some fields are locked because an assessment is in progress for this application.
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => { setShowEditApplicationModal(false); setEditingApplication(null); }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      <AssessmentUnassignedModal
        isOpen={showUnassignedAlert}
        onClose={() => setShowUnassignedAlert(false)}
      />
    </div>
  );
};

export default BusinessUnits;
